import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { useAuth, isSuperAdmin } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';
import { normalizeCurrency } from '@/lib/currency';
import { uploadDigitalFile, DIGITAL_FILE_LIMIT_LABEL } from '@/hooks/useUpload';
import {
  listAccessForProduct,
  listZifekClientsByIds,
  searchZifekClients,
  grantDigitalAccess,
  revokeDigitalAccess,
  type DigitalAccessEntry,
  type ZifekClient,
} from '@/lib/digitalAccess';
import { getStoreCapabilities } from '@/lib/accountType';
import { MENU_ROLES } from '@/lib/menuRole';
import { PRODUCT_VISIBILITIES, normalizeTaxConfig } from '@/lib/coreCatalog';
import { getOrganizationByCommerce } from '@/lib/zifekCore';
import { emitCoreActivity } from '@/lib/coreEventHooks';
import { setEntityTags, listTagIdsForEntity } from '@/lib/coreSocle';
import { listCoreLanguages } from '@/lib/coreSystem';
import TagSelector from '@/components/feature/TagSelector';
import MediaLibraryPicker from '@/components/feature/MediaLibraryPicker';
import VoiceInputButton from '@/components/feature/VoiceInputButton';
import { insertTranscriptAtCursor } from '@/lib/voiceInsert';
import TranslationEditor, { type EntityTranslation, type TranslationLanguage } from '@/components/feature/TranslationEditor';

interface ProductCategory {
  id: number;
  name: string;
  image_couverture?: string;
  icon?: string;
}

interface ProductItem {
  id: number;
  name: string;
  description: string | null;
  category_id: number | null;
  status: string;
  price: number;
  stock: number;
  discount_enabled: boolean;
  discount_price: number | null;
  media: { url: string; type: string }[] | null;
  pricing_mode: number;
  product_type: string | null;
  digital_file_url: string | null;
  digital_file_name: string | null;
  digital_delivery_note: string | null;
  seo_title?: string | null;
  seo_description?: string | null;
  seo_keywords?: string | null;
  menu_role?: string | null;
  sku?: string | null;
  barcode?: string | null;
  subcategory?: string | null;
  visibility?: string | null;
  stock_tracking_enabled?: boolean | null;
  tax_config?: unknown;
}

interface ExistingVariant {
  id: number;
  name: string;
  options: string[];
}

interface ExistingSku {
  id: number;
  label: string;
  options: string[];
  price: number | null;
  stock: number;
  discount_enabled: boolean;
  discount_price: number | null;
}

interface VariantDef {
  id: string;
  name: string;
  optionsText: string;
  existingId?: number;
}

interface SkuRow {
  key: string;
  label: string;
  options: string[];
  price: string;
  stock: string;
  discount_enabled: boolean;
  discount_price: string;
  existingId?: number;
}

type PricingMode = 'same' | 'per_variant';

export default function EditProductPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const productId = parseInt(id || '0', 10);
  const { t } = useTranslation();
  // Devise unique du marchand : choisie une seule fois dans Paramètres → Profil.
  const currency = normalizeCurrency(user?.monaie || 'MAD');
  // Le sélecteur « Rôle au menu » n'apparaît que pour les comptes Restauration.
  const caps = getStoreCapabilities(user?.type, user?.typecompte);

  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [status, setStatus] = useState('draft');
  const [price, setPrice] = useState('');
  const [stock, setStock] = useState('0');
  const [costPrice, setCostPrice] = useState('');
  const [discountEnabled, setDiscountEnabled] = useState(false);
  const [discountPrice, setDiscountPrice] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [seoKeywords, setSeoKeywords] = useState('');
  const [productType, setProductType] = useState<'physical' | 'digital'>('physical');
  const [menuRole, setMenuRole] = useState<string>('');
  const [digitalUrl, setDigitalUrl] = useState('');
  const [digitalFileName, setDigitalFileName] = useState('');
  const [digitalNote, setDigitalNote] = useState('');
  const [digitalUploading, setDigitalUploading] = useState(false);
  const digitalInputRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const [sku, setSku] = useState('');
  const [barcode, setBarcode] = useState('');
  const [subcategory, setSubcategory] = useState('');
  const [visibility, setVisibility] = useState('public');
  const [stockTracking, setStockTracking] = useState(true);
  const [taxRate, setTaxRate] = useState('');
  const [taxIncluded, setTaxIncluded] = useState(false);

  // Socle central : tags (core_tags) + médiathèque (images)
  const [orgId, setOrgId] = useState<number | null>(null);
  const [tagIds, setTagIds] = useState<number[]>([]);
  // Socle Langues : versions traduites (intitulé/description par langue)
  const [translationLangs, setTranslationLangs] = useState<TranslationLanguage[]>([]);
  const [translations, setTranslations] = useState<EntityTranslation>({});
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [pricingMode, setPricingMode] = useState<PricingMode>('same');
  const [hasVariants, setHasVariants] = useState(false);
  const [variantDefs, setVariantDefs] = useState<VariantDef[]>([]);
  const [skus, setSkus] = useState<SkuRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Accès clients (produits digitaux)
  const [accessList, setAccessList] = useState<DigitalAccessEntry[]>([]);
  const [accessClients, setAccessClients] = useState<Record<number, ZifekClient>>({});
  const [clientQuery, setClientQuery] = useState('');
  const [clientResults, setClientResults] = useState<ZifekClient[]>([]);
  const [clientSearching, setClientSearching] = useState(false);
  const [accessBusy, setAccessBusy] = useState(false);
  const [accessMsg, setAccessMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !productId) return;
    const load = async () => {
      setLoading(true);
      try {
        const { data: cats } = await supabase
          .from('categorie')
          .select('id, nom')
          .eq('idcommerce', getCommerceId(user))
          .order('nom');
        setCategories((cats || []).map((c: { id: number; nom: string }) => ({ id: c.id, name: c.nom })));

        // Isolation : un marchand n'ouvre que SES produits. Le super-admin garde l'accès global.
        let productQuery = supabase.from('product_items').select('*').eq('id', productId);
        if (!isSuperAdmin(user.typecompte)) productQuery = productQuery.eq('idcommerce', getCommerceId(user));
        const { data: product, error: prodErr } = await productQuery.maybeSingle();
        if (prodErr || !product) { navigate('/dashboard/products'); return; }

        setName(product.name || '');
        setDescription(product.description || '');
        setCategoryId(product.category_id ? String(product.category_id) : '');
        setStatus(product.status || 'draft');
        setPrice(String(product.price || ''));
        setStock(String(product.stock || 0));
        setCostPrice(product.cost_price != null ? String(product.cost_price) : '');
        setDiscountEnabled(product.discount_enabled || false);
        setDiscountPrice(product.discount_price ? String(product.discount_price) : '');
        if (product.media && product.media.length > 0) setMediaUrl(product.media[0].url || '');
        setSeoTitle(product.seo_title || '');
        setSeoDescription(product.seo_description || '');
        setSeoKeywords(product.seo_keywords || '');
        setProductType(product.product_type === 'digital' ? 'digital' : 'physical');
        setMenuRole(product.menu_role || '');
        setDigitalUrl(product.digital_file_url || '');
        setDigitalFileName(product.digital_file_name || '');
        setDigitalNote(product.digital_delivery_note || '');
        setSku(product.sku || '');
        setBarcode(product.barcode || '');
        setSubcategory(product.subcategory || '');
        setVisibility(product.visibility || 'public');
        setStockTracking(product.stock_tracking_enabled !== false);
        const tax = normalizeTaxConfig(product.tax_config);
        setTaxRate(tax.rate ? String(tax.rate) : '');
        setTaxIncluded(!!tax.included);
        setPricingMode(product.pricing_mode === 1 ? 'per_variant' : 'same');
        const tr = (product as { translation?: unknown }).translation;
        if (tr && typeof tr === 'object') setTranslations(tr as EntityTranslation);
        else if (typeof tr === 'string' && tr.trim()) { try { setTranslations(JSON.parse(tr) as EntityTranslation); } catch { /* ignore */ } }

        const { data: variants } = await supabase.from('product_variants').select('*').eq('product_id', productId).order('sort_order');
        if (variants && variants.length > 0) {
          setHasVariants(true);
          setVariantDefs(variants.map((v) => ({
            id: crypto.randomUUID(), name: v.name, optionsText: (v.options || []).join(', '), existingId: v.id,
          })));
        }

        const { data: existingSkus } = await supabase.from('product_skus').select('*').eq('product_id', productId);
        if (existingSkus && existingSkus.length > 0) {
          setSkus(existingSkus.map((s) => ({
            key: crypto.randomUUID(),
            label: s.label,
            options: s.options || [],
            price: s.price ? String(s.price) : '',
            stock: String(s.stock || 0),
            discount_enabled: s.discount_enabled || false,
            discount_price: s.discount_price ? String(s.discount_price) : '',
            existingId: s.id,
          })));
        }
      } catch { /* silent */ } finally { setLoading(false); }
    };
    load();
  }, [user, productId, navigate]);

  useEffect(() => {
    if (!user || !productId) return;
    const cid = getCommerceId(user);
    if (!cid) return;
    (async () => {
      try {
        const org = await getOrganizationByCommerce(cid);
        if (org) {
          setOrgId(org.id);
          setTagIds(await listTagIdsForEntity('product', productId));
        }
      } catch { /* best-effort */ }
    })();
  }, [user, productId]);

  useEffect(() => {
    listCoreLanguages()
      .then((langs) => setTranslationLangs(
        langs.filter((l) => l.active && !l.is_default).map((l) => ({ code: l.code, label: l.label, flag: l.flag, isDefault: false })),
      ))
      .catch(() => setTranslationLangs([]));
  }, []);

  const generateCombinations = (defs: VariantDef[]): string[][] => {
    if (defs.length === 0 || defs.some((d) => !d.optionsText.trim())) return [];
    const optionArrays = defs.map((d) => d.optionsText.split(',').map((o) => o.trim()).filter(Boolean));
    if (optionArrays.some((arr) => arr.length === 0)) return [];
    let combos: string[][] = [[]];
    for (const arr of optionArrays) {
      const next: string[][] = [];
      for (const combo of combos) { for (const opt of arr) { next.push([...combo, opt]); } }
      combos = next;
    }
    return combos;
  };

  const rebuildSkus = (defs: VariantDef[]) => {
    const combos = generateCombinations(defs);
    const currentMap = new Map<string, SkuRow>();
    skus.forEach((s) => currentMap.set(s.label, s));

    setSkus(combos.map((combo) => {
      const label = combo.join(' / ');
      const existing = currentMap.get(label);
      return existing || {
        key: crypto.randomUUID(),
        label,
        options: combo,
        price: '',
        stock: '',
        discount_enabled: false,
        discount_price: '',
      };
    }));
  };

  const addVariantDef = () => {
    const updated = [...variantDefs, { id: crypto.randomUUID(), name: '', optionsText: '' }];
    setVariantDefs(updated);
  };

  const removeVariantDef = (id: string) => {
    const updated = variantDefs.filter((d) => d.id !== id);
    setVariantDefs(updated);
    rebuildSkus(updated);
  };

  const updateVariantDef = (id: string, field: 'name' | 'optionsText', value: string) => {
    const updated = variantDefs.map((d) => (d.id === id ? { ...d, [field]: value } : d));
    setVariantDefs(updated);
    if (field === 'optionsText') rebuildSkus(updated);
  };

  const updateSkuField = (index: number, field: keyof SkuRow, value: string | boolean) => {
    setSkus((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  };

  const handleDigitalUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setDigitalUploading(true);
    setMsg(null);
    try {
      const url = await uploadDigitalFile(f);
      setDigitalUrl(url);
      setDigitalFileName(f.name);
    } catch (err: unknown) {
      setMsg({ type: 'error', text: err instanceof Error ? err.message : 'Erreur lors du téléversement' });
    } finally {
      setDigitalUploading(false);
      if (digitalInputRef.current) digitalInputRef.current.value = '';
    }
  };

  // ── Accès clients (produits digitaux) ─────────────────────────────
  const loadAccess = useCallback(async () => {
    if (!productId) return;
    try {
      const rows = await listAccessForProduct(productId);
      setAccessList(rows);
      const clients = await listZifekClientsByIds(rows.map((r) => r.userId));
      const map: Record<number, ZifekClient> = {};
      clients.forEach((c) => { map[c.id] = c; });
      setAccessClients(map);
    } catch {
      /* silencieux */
    }
  }, [productId]);

  useEffect(() => {
    if (productType === 'digital') loadAccess();
  }, [productType, loadAccess]);

  useEffect(() => {
    const q = clientQuery.trim();
    if (q.length < 2) {
      setClientResults([]);
      return;
    }
    let cancelled = false;
    setClientSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await searchZifekClients(q, 8);
        if (!cancelled) setClientResults(res);
      } catch {
        if (!cancelled) setClientResults([]);
      } finally {
        if (!cancelled) setClientSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [clientQuery]);

  const handleGrantAccess = async (client: ZifekClient) => {
    if (!productId) return;
    setAccessBusy(true);
    setAccessMsg(null);
    try {
      await grantDigitalAccess({
        productId,
        productName: name.trim() || null,
        userId: client.id,
        merchantId: getCommerceId(user),
        source: 'manual',
      });
      setAccessMsg(`Accès accordé à ${client.name}.`);
      setClientQuery('');
      setClientResults([]);
      await loadAccess();
    } catch (err: unknown) {
      setAccessMsg(err instanceof Error ? err.message : "Erreur lors de l'attribution de l'accès.");
    } finally {
      setAccessBusy(false);
    }
  };

  const handleRevokeAccess = async (entry: DigitalAccessEntry) => {
    setAccessBusy(true);
    setAccessMsg(null);
    try {
      await revokeDigitalAccess(entry.id);
      setAccessMsg('Accès retiré.');
      await loadAccess();
    } catch (err: unknown) {
      setAccessMsg(err instanceof Error ? err.message : 'Erreur lors du retrait.');
    } finally {
      setAccessBusy(false);
    }
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = t('prf.name_required', { defaultValue: 'Le nom est requis' });
    if (!price.trim() || isNaN(Number(price)) || Number(price) < 0) errs.price = t('prf.invalid_price', { defaultValue: 'Prix invalide' });
    if (stock.trim() && (isNaN(Number(stock)) || Number(stock) < 0)) errs.stock = t('prf.invalid_stock', { defaultValue: 'Stock invalide' });
    if (discountEnabled && discountPrice.trim() && (isNaN(Number(discountPrice)) || Number(discountPrice) < 0)) errs.discountPrice = t('prf.invalid_promo', { defaultValue: 'Prix promo invalide' });
    if (hasVariants && pricingMode === 'per_variant') {
      for (let i = 0; i < skus.length; i++) {
        const s = skus[i];
        if (!s.price.trim() || isNaN(Number(s.price)) || Number(s.price) < 0) errs[`sku_price_${i}`] = `Prix invalide pour "${s.label}"`;
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    if (!user) return;
    setSaving(true);
    setMsg(null);

    try {
      const media: { url: string; type: string }[] = mediaUrl.trim() ? [{ url: mediaUrl.trim(), type: 'image' }] : [];

      let productUpdate = supabase.from('product_items').update({
        name: name.trim(),
        description: description.trim() || null,
        category_id: categoryId ? parseInt(categoryId, 10) : null,
        status,
        price: parseFloat(price) || 0,
        cost_price: costPrice.trim() ? parseFloat(costPrice) : null,
        currency,
        stock: productType === 'digital' ? 0 : (parseInt(stock, 10) || 0),
        product_type: productType,
        digital_file_url: productType === 'digital' ? (digitalUrl.trim() || null) : null,
        digital_file_name: productType === 'digital' ? (digitalFileName.trim() || null) : null,
        digital_delivery_note: productType === 'digital' ? (digitalNote.trim() || null) : null,
        discount_enabled: discountEnabled,
        discount_price: discountEnabled && discountPrice.trim() ? parseFloat(discountPrice) : null,
        pricing_mode: hasVariants ? (pricingMode === 'per_variant' ? 1 : 0) : 0,
        menu_role: menuRole || null,
        media: JSON.stringify(media),
        sku: sku.trim() || null,
        barcode: barcode.trim() || null,
        subcategory: subcategory.trim() || null,
        translation: translations,
        visibility,
        stock_tracking_enabled: productType === 'physical' ? stockTracking : false,
        tax_config: { rate: parseFloat(taxRate) || 0, included: taxIncluded },
        seo_title: seoTitle.trim() || null,
        seo_description: seoDescription.trim() || null,
        seo_keywords: seoKeywords.trim() || null,
      }).eq('id', productId);
      if (!isSuperAdmin(user.typecompte)) productUpdate = productUpdate.eq('idcommerce', getCommerceId(user));
      const { error: updateErr } = await productUpdate;
      if (updateErr) throw updateErr;

      // Handle variants: delete all, re-insert
      await supabase.from('product_variants').delete().eq('product_id', productId);
      await supabase.from('product_skus').delete().eq('product_id', productId);

      if (hasVariants && variantDefs.length > 0) {
        for (let i = 0; i < variantDefs.length; i++) {
          const def = variantDefs[i];
          const opts = def.optionsText.split(',').map((o) => o.trim()).filter(Boolean);
          await supabase.from('product_variants').insert({ product_id: productId, name: def.name.trim(), options: JSON.stringify(opts), sort_order: i });
        }

        const skuPayloads = skus.map((s) => ({
          product_id: productId,
          label: s.label,
          options: JSON.stringify(s.options),
          price: pricingMode === 'per_variant' ? parseFloat(s.price) || 0 : null,
          stock: parseInt(s.stock, 10) || 0,
          discount_enabled: s.discount_enabled,
          discount_price: s.discount_enabled && s.discount_price.trim() ? parseFloat(s.discount_price) : null,
        }));
        if (skuPayloads.length > 0) await supabase.from('product_skus').insert(skuPayloads);
      }

      if (orgId) {
        try { await setEntityTags(orgId, 'product', productId, tagIds); } catch { /* best-effort */ }
      }

      // Événement du socle : produit modifié → re-index auto + consommateurs.
      await emitCoreActivity({
        commerceId: getCommerceId(user),
        eventKey: 'product.updated',
        entityType: 'product',
        entityId: productId,
        title: `Produit modifié — ${name.trim()}`,
        description: description.trim() || null,
        amount: parseFloat(price) || 0,
        currency,
        payload: { name: name.trim(), price: parseFloat(price) || 0, status },
        actorName: user?.name ?? null,
        actorUserId: user?.id ?? null,
        source: 'products',
      });

      setMsg({ type: 'success', text: t('prf.updated', { defaultValue: 'Produit mis à jour avec succès !' }) });
      setTimeout(() => navigate('/dashboard/products'), 1200);
    } catch (err: unknown) {
      setMsg({ type: 'error', text: err instanceof Error ? err.message : 'Erreur lors de la mise à jour' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 flex items-center justify-center py-20">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-4xl">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/dashboard/products')} className="w-9 h-9 flex items-center justify-center rounded-full bg-background-50 border border-background-200/70 text-foreground-500 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer">
          <i className="ri-arrow-left-line"></i>
        </button>
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">{t('prf.edit_title', { defaultValue: 'Modifier le produit' })}</h2>
          <p className="text-sm text-foreground-500 mt-1">{name || t('common.loading', { defaultValue: 'Chargement...' })}</p>
        </div>
      </div>

      {msg && (
        <div className={`mb-5 px-4 py-3 rounded-lg text-sm font-medium ${msg.type === 'success' ? 'bg-accent-50 text-accent-700' : 'bg-red-50 text-red-600'}`}>
          {msg.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 space-y-5">
          <h3 className="text-base font-bold font-heading text-foreground-900 pb-3 border-b border-background-200/70">{t('prf.general_info', { defaultValue: 'Informations générales' })}</h3>

          <div>
            <label className="block text-sm font-semibold text-foreground-800 mb-2">Type de produit</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button type="button" onClick={() => setProductType('physical')}
                className={`text-left p-3 rounded-lg border transition-colors cursor-pointer ${productType === 'physical' ? 'border-primary-400 bg-primary-50/50' : 'border-background-200/70 hover:border-background-300/60'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <i className={`ri-box-3-line text-base ${productType === 'physical' ? 'text-primary-600' : 'text-foreground-400'}`}></i>
                  <span className="text-sm font-semibold text-foreground-900">Produit physique</span>
                </div>
                <span className="text-xs text-foreground-500">Stock, livraison, expédition</span>
              </button>
              <button type="button" onClick={() => setProductType('digital')}
                className={`text-left p-3 rounded-lg border transition-colors cursor-pointer ${productType === 'digital' ? 'border-secondary-400 bg-secondary-50/50' : 'border-background-200/70 hover:border-background-300/60'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <i className={`ri-download-cloud-2-line text-base ${productType === 'digital' ? 'text-secondary-600' : 'text-foreground-400'}`}></i>
                  <span className="text-sm font-semibold text-foreground-900">Produit digital</span>
                </div>
                <span className="text-xs text-foreground-500">Livre, vidéo, PDF… livré par fichier</span>
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground-800 mb-1.5">{t('prf.product_name', { defaultValue: 'Nom du produit' })} <span className="text-red-500">*</span></label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)}
              className={`w-full px-3 py-2.5 bg-background-50 border rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors ${errors.name ? 'border-red-300' : 'border-background-200/70'}`} />
            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
          </div>

          <div>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <label className="block text-sm font-semibold text-foreground-800">{t('prf.description', { defaultValue: 'Description' })}</label>
              <VoiceInputButton
                userId={user?.id}
                title="Dicter la description"
                onTranscript={(text) => {
                  setDescription((prev) => insertTranscriptAtCursor(descriptionRef.current, prev, text));
                  descriptionRef.current?.focus();
                }}
              />
            </div>
            <textarea ref={descriptionRef} value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
              className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors resize-none" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-semibold text-foreground-800 mb-1.5">{t('prf.category', { defaultValue: 'Catégorie' })}</label>
              <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}
                className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer">
                <option value="">{t('prf.no_category', { defaultValue: 'Aucune catégorie' })}</option>
                {categories.map((cat) => (<option key={cat.id} value={String(cat.id)}>{cat.name}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground-800 mb-1.5">{t('prf.status', { defaultValue: 'Statut' })}</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}
                className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer">
                <option value="draft">{t('prf.status_draft', { defaultValue: 'Brouillon' })}</option>
                <option value="active">{t('prf.status_active', { defaultValue: 'Actif' })}</option>
                <option value="inactive">{t('prf.status_inactive', { defaultValue: 'Inactif' })}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground-800 mb-1.5">{t('prf.price', { defaultValue: 'Prix' })} ({currency}) <span className="text-red-500">*</span></label>
              <input type="number" step="0.01" min="0" value={price} onChange={(e) => setPrice(e.target.value)}
                className={`w-full px-3 py-2.5 bg-background-50 border rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors ${errors.price ? 'border-red-300' : 'border-background-200/70'}`} />
              {errors.price && <p className="text-xs text-red-500 mt-1">{errors.price}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground-800 mb-1.5">{t('prf.currency', { defaultValue: 'Devise' })}</label>
              <div className="flex items-center gap-2 w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-700" title="Devise unique de votre compte">
                <i className="ri-lock-line text-foreground-400"></i>
                <span className="font-semibold">{currency}</span>
              </div>
              <p className="text-[11px] text-foreground-400 mt-1">Définie dans Paramètres&nbsp;→&nbsp;Profil.</p>
            </div>
          </div>

          {caps.restaurant && (
          <div>
            <label className="block text-sm font-semibold text-foreground-800 mb-1.5">Rôle au menu</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              <button
                type="button"
                onClick={() => setMenuRole('')}
                className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${menuRole === '' ? 'border-primary-400 bg-primary-50/60 text-primary-700' : 'border-background-200/70 text-foreground-600 hover:bg-background-100'}`}
              >
                <i className="ri-forbid-2-line"></i>
                Aucun
              </button>
              {MENU_ROLES.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => setMenuRole(r.key)}
                  className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${menuRole === r.key ? 'border-primary-400 bg-primary-50/60 text-primary-700' : 'border-background-200/70 text-foreground-600 hover:bg-background-100'}`}
                >
                  <i className={r.icon}></i>
                  {r.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-foreground-400 mt-1.5">Structure votre carte : les plats sont regroupés par section (entrées, plats, desserts, boissons) sur votre site public.</p>
          </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {productType === 'physical' && (
            <div>
              <label className="block text-sm font-semibold text-foreground-800 mb-1.5">{t('prf.stock', { defaultValue: 'Stock' })}</label>
              <input type="number" min="0" value={stock} onChange={(e) => setStock(e.target.value)}
                className={`w-full px-3 py-2.5 bg-background-50 border rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors ${errors.stock ? 'border-red-300' : 'border-background-200/70'}`} />
              {errors.stock && <p className="text-xs text-red-500 mt-1">{errors.stock}</p>}
            </div>
            )}
            <div>
              <label className="block text-sm font-semibold text-foreground-800 mb-1.5">{t('prf.cost_price', { defaultValue: 'Coût d’achat' })} ({currency})</label>
              <input type="number" step="0.01" min="0" value={costPrice} onChange={(e) => setCostPrice(e.target.value)}
                className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors" />
              <p className="text-[11px] text-foreground-400 mt-1">Utilisé pour calculer votre marge et votre rentabilité.</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground-800 mb-1.5">{t('prf.image_url', { defaultValue: 'Image (URL)' })}</label>
            <input type="url" value={mediaUrl} onChange={(e) => setMediaUrl(e.target.value)}
              className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors" />
            <button type="button" onClick={() => setLibraryOpen(true)} className="mt-2 inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-secondary-50 text-secondary-700 text-xs font-semibold whitespace-nowrap hover:bg-secondary-100 transition-colors cursor-pointer">
              <i className="ri-image-2-line"></i> Choisir depuis la médiathèque
            </button>
          </div>

          {mediaUrl && (
            <div className="w-24 h-24 rounded-lg border border-background-200/70 overflow-hidden bg-background-100">
              <img src={mediaUrl} alt="Aperçu" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            </div>
          )}

          <div className="bg-background-100/70 rounded-lg p-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={discountEnabled} onChange={(e) => setDiscountEnabled(e.target.checked)}
                className="w-4 h-4 rounded border-background-300/60 text-primary-500 focus:ring-primary-400 cursor-pointer" />
              <span className="text-sm font-semibold text-foreground-800">{t('prf.enable_promo', { defaultValue: 'Activer la promotion' })}</span>
            </label>
            {discountEnabled && (
              <div className="mt-3">
                <label className="block text-sm font-medium text-foreground-700 mb-1">{t('prf.promo_price', { defaultValue: 'Prix promotionnel' })} ({currency})</label>
                <input type="number" step="0.01" min="0" value={discountPrice} onChange={(e) => setDiscountPrice(e.target.value)}
                  className={`w-full px-3 py-2.5 bg-background-50 border rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors ${errors.discountPrice ? 'border-red-300' : 'border-background-200/70'}`} />
                {errors.discountPrice && <p className="text-xs text-red-500 mt-1">{errors.discountPrice}</p>}
              </div>
            )}
          </div>
        </div>

        {/* Entité centrale — champs partagés par tous les modules */}
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 space-y-5">
          <div className="flex items-start gap-3 pb-3 border-b border-background-200/70">
            <div className="w-9 h-9 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center flex-shrink-0">
              <i className="ri-database-2-line"></i>
            </div>
            <div>
              <h3 className="text-base font-bold font-heading text-foreground-900">Entité centrale</h3>
              <p className="text-xs text-foreground-500 mt-0.5">Champs partagés par tous les modules Zifek (Commerce, Caisse, Stock, Commandes, Marketing, Finance).</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-foreground-800 mb-1.5">Référence (SKU)</label>
              <input type="text" value={sku} onChange={(e) => setSku(e.target.value)} placeholder="Ex : TSH-001"
                className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground-800 mb-1.5">Code-barres</label>
              <input type="text" value={barcode} onChange={(e) => setBarcode(e.target.value)} placeholder="EAN / UPC"
                className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground-800 mb-1.5">Sous-catégorie</label>
              <input type="text" value={subcategory} onChange={(e) => setSubcategory(e.target.value)} placeholder="Ex : T-shirts"
                className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-foreground-800 mb-1.5">Visibilité</label>
              <select value={visibility} onChange={(e) => setVisibility(e.target.value)}
                className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer">
                {PRODUCT_VISIBILITIES.map((v) => (<option key={v.key} value={v.key}>{v.label}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground-800 mb-1.5">Taux de TVA (%)</label>
              <input type="number" step="0.1" min="0" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} placeholder="0"
                className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors" />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-3 cursor-pointer pb-2.5">
                <input type="checkbox" checked={taxIncluded} onChange={(e) => setTaxIncluded(e.target.checked)}
                  className="w-4 h-4 rounded border-background-300/60 text-primary-500 focus:ring-primary-400 cursor-pointer" />
                <span className="text-sm text-foreground-700 whitespace-nowrap">Prix TTC (taxe incluse)</span>
              </label>
            </div>
          </div>

          {productType === 'physical' && (
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={stockTracking} onChange={(e) => setStockTracking(e.target.checked)}
                className="w-4 h-4 rounded border-background-300/60 text-primary-500 focus:ring-primary-400 cursor-pointer" />
              <span className="text-sm text-foreground-700">Suivi du stock activé (les fonctions avancées restent dans Zifek Inventory)</span>
            </label>
          )}

          <TagSelector
            organizationId={orgId}
            value={tagIds}
            onChange={setTagIds}
            scope="product"
            label="Tags"
          />

          <TranslationEditor
            languages={translationLangs}
            value={translations}
            onChange={setTranslations}
            label="Traductions du produit"
            hint="Renseignez l'intitulé et la description du produit dans chaque langue (une seule fiche, plusieurs versions)."
          />
        </div>

        {/* Fichier digital */}
        {productType === 'digital' && (
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 space-y-5">
          <h3 className="text-base font-bold font-heading text-foreground-900 pb-3 border-b border-background-200/70">Fichier digital</h3>
          <input ref={digitalInputRef} type="file" onChange={handleDigitalUpload} className="hidden" />
          {digitalUrl ? (
            <div className="flex items-center gap-3 p-3 rounded-lg border border-background-200/70 bg-background-100/50">
              <div className="w-11 h-11 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-file-line text-xl text-primary-500"></i>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground-800 truncate">{digitalFileName || 'Fichier'}</p>
                <a href={digitalUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary-600 hover:underline truncate block">Voir le fichier</a>
              </div>
              <button type="button" onClick={() => digitalInputRef.current?.click()} disabled={digitalUploading}
                className="px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap">
                Remplacer
              </button>
            </div>
          ) : (
            <button type="button" onClick={() => digitalInputRef.current?.click()} disabled={digitalUploading}
              className="w-full border-2 border-dashed border-background-300/60 rounded-xl p-6 flex flex-col items-center gap-2 cursor-pointer hover:border-primary-300/50 hover:bg-background-100/50 transition-colors disabled:opacity-50">
              {digitalUploading ? (
                <i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i>
              ) : (
                <i className="ri-upload-cloud-2-line text-2xl text-foreground-400"></i>
              )}
              <span className="text-sm text-foreground-600 font-medium">Téléversez le fichier à livrer</span>
              <span className="text-xs text-foreground-400">PDF, vidéo, ZIP… Max {DIGITAL_FILE_LIMIT_LABEL}</span>
            </button>
          )}
          <div>
            <label className="block text-sm font-semibold text-foreground-800 mb-1.5">Note de livraison (optionnel)</label>
            <textarea value={digitalNote} onChange={(e) => setDigitalNote(e.target.value)} rows={2} maxLength={500}
              placeholder="Ex : Le lien de téléchargement apparaîtra après votre achat."
              className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none" />
          </div>
          <p className="text-xs text-foreground-400">Les produits digitaux n&apos;ont ni stock ni adresse de livraison : le fichier est mis à disposition de l&apos;acheteur après sa commande.</p>
        </div>
        )}

        {/* Accès clients (manuel) — produits digitaux */}
        {productType === 'digital' && (
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 space-y-5">
          <div className="flex items-center gap-2 pb-3 border-b border-background-200/70">
            <i className="ri-key-2-line text-secondary-600 text-lg"></i>
            <h3 className="text-base font-bold font-heading text-foreground-900">Accès clients</h3>
          </div>

          <p className="text-xs text-foreground-500">
            Donnez l&apos;accès au fichier à un client Zifek sans achat en ligne (paiement hors ligne, geste commercial…).
            L&apos;accès est aussi accordé automatiquement lorsque vous validez le paiement d&apos;une commande depuis l&apos;onglet Commandes.
          </p>

          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="text"
              value={clientQuery}
              onChange={(e) => setClientQuery(e.target.value)}
              placeholder="Rechercher un client (nom, pseudo ou email)"
              className="w-full pl-10 pr-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
            />
            {clientQuery.trim().length >= 2 && (
              <div className="absolute z-30 mt-1 w-full bg-background-50 border border-background-200/70 rounded-lg shadow-lg max-h-64 overflow-y-auto py-1">
                {clientSearching ? (
                  <div className="flex items-center gap-2 px-3 py-2.5 text-xs text-foreground-500">
                    <i className="ri-loader-4-line animate-spin"></i>Recherche...
                  </div>
                ) : clientResults.length === 0 ? (
                  <div className="px-3 py-2.5 text-xs text-foreground-400">Aucun client trouvé.</div>
                ) : (
                  clientResults.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => handleGrantAccess(c)}
                      disabled={accessBusy}
                      className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50"
                    >
                      <div className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {c.image ? (
                          <img src={c.image} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <i className="ri-user-line text-foreground-400 text-sm"></i>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground-900 truncate">{c.name}</p>
                        <p className="text-xs text-foreground-400 truncate">{c.email || `@${c.userName}`}</p>
                      </div>
                      <span className="text-xs font-semibold text-primary-600 whitespace-nowrap">Donner accès</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {accessMsg && (
            <div className="px-3 py-2 rounded-lg text-xs bg-accent-50 text-accent-700">{accessMsg}</div>
          )}

          <div>
            <p className="text-xs font-semibold text-foreground-500 mb-2">
              Clients ayant accès ({accessList.length})
            </p>
            {accessList.length === 0 ? (
              <p className="text-xs text-foreground-400 py-2">Aucun client n&apos;a encore accès à ce produit.</p>
            ) : (
              <div className="space-y-2">
                {accessList.map((entry) => {
                  const c = accessClients[entry.userId];
                  return (
                    <div key={entry.id} className="flex items-center gap-3 px-3 py-2 rounded-lg border border-background-200/70 bg-background-50">
                      <div className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {c?.image ? (
                          <img src={c.image} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <i className="ri-user-line text-foreground-400 text-sm"></i>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground-900 truncate">
                          {c?.name || `Client #${entry.userId}`}
                        </p>
                        <p className="text-[11px] text-foreground-400 truncate">
                          {c?.email || ''}
                          {entry.source === 'payment' ? ' · via paiement' : ' · manuel'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRevokeAccess(entry)}
                        disabled={accessBusy}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap"
                        title="Retirer l'accès"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        )}

        {/* Variantes */}
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 space-y-5">
          <div className="flex items-center justify-between pb-3 border-b border-background-200/70">
            <h3 className="text-base font-bold font-heading text-foreground-900">{t('prf.variants', { defaultValue: 'Variantes' })}</h3>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={hasVariants} onChange={(e) => { setHasVariants(e.target.checked); if (!e.target.checked) { setVariantDefs([]); setSkus([]); } }}
                className="w-4 h-4 rounded border-background-300/60 text-primary-500 focus:ring-primary-400 cursor-pointer" />
              <span className="text-sm text-foreground-600">{t('prf.has_variants', { defaultValue: 'Ce produit a des variantes (tailles, couleurs...)' })}</span>
            </label>
          </div>

          {hasVariants && (
            <>
              <div className="space-y-3">
                {variantDefs.map((def) => (
                  <div key={def.id} className="flex items-start gap-3 p-3 bg-background-100/70 rounded-lg">
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <input type="text" value={def.name} onChange={(e) => updateVariantDef(def.id, 'name', e.target.value)} placeholder={t('prf.variant_type_ph', { defaultValue: 'Type (ex: Taille)' })}
                        className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300" />
                      <input type="text" value={def.optionsText} onChange={(e) => updateVariantDef(def.id, 'optionsText', e.target.value)} placeholder={t('prf.variant_options_ph', { defaultValue: 'Options (ex: S, M, L)' })}
                        className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300" />
                    </div>
                    <button type="button" onClick={() => removeVariantDef(def.id)}
                      className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer flex-shrink-0 mt-0.5">
                      <i className="ri-close-line"></i>
                    </button>
                  </div>
                ))}
                <button type="button" onClick={addVariantDef}
                  className="flex items-center gap-2 px-4 py-2 bg-secondary-50 text-secondary-700 rounded-full text-sm font-medium whitespace-nowrap hover:bg-secondary-100 transition-colors cursor-pointer">
                  <i className="ri-add-line"></i> {t('prf.add_variant_type', { defaultValue: 'Ajouter un type de variante' })}
                </button>
              </div>

              {skus.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-foreground-800">{t('prf.combination', { defaultValue: 'Combinaisons' })} ({skus.length})</span>
                    <div className="flex items-center gap-2 ml-auto">
                      <span className="text-xs text-foreground-500">{t('prf.pricing', { defaultValue: 'Tarification :' })}</span>
                      <button type="button" onClick={() => setPricingMode('same')}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${pricingMode === 'same' ? 'bg-primary-50 text-primary-700' : 'bg-background-100 text-foreground-500 hover:bg-background-200/70'}`}>
                        {t('prf.single_price', { defaultValue: 'Prix unique' })}
                      </button>
                      <button type="button" onClick={() => setPricingMode('per_variant')}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${pricingMode === 'per_variant' ? 'bg-primary-50 text-primary-700' : 'bg-background-100 text-foreground-500 hover:bg-background-200/70'}`}>
                        {t('prf.per_variant', { defaultValue: 'Prix par variante' })}
                      </button>
                    </div>
                  </div>

                  <div className="overflow-x-auto rounded-lg border border-background-200/70">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-background-100 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                          <th className="text-left px-4 py-2.5">{t('prf.combination', { defaultValue: 'Combinaison' })}</th>
                          {pricingMode === 'per_variant' && <th className="text-left px-4 py-2.5">{t('prf.price', { defaultValue: 'Prix' })} ({currency})</th>}
                          <th className="text-left px-4 py-2.5">{t('prf.stock', { defaultValue: 'Stock' })}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-background-200/70">
                        {skus.map((sku, idx) => (
                          <tr key={sku.key} className="hover:bg-background-50/50 transition-colors">
                            <td className="px-4 py-2.5"><span className="text-foreground-800 font-medium">{sku.label}</span></td>
                            {pricingMode === 'per_variant' && (
                              <td className="px-4 py-2.5">
                                <input type="number" step="0.01" min="0" value={sku.price} onChange={(e) => updateSkuField(idx, 'price', e.target.value)}
                                  className={`w-24 px-2 py-1.5 bg-background-50 border rounded text-sm text-foreground-900 focus:outline-none focus:border-primary-300 ${errors[`sku_price_${idx}`] ? 'border-red-300' : 'border-background-200/70'}`} />
                                {errors[`sku_price_${idx}`] && <p className="text-xs text-red-500 mt-0.5">{errors[`sku_price_${idx}`]}</p>}
                              </td>
                            )}
                            <td className="px-4 py-2.5">
                              <input type="number" min="0" value={sku.stock} onChange={(e) => updateSkuField(idx, 'stock', e.target.value)}
                                className="w-20 px-2 py-1.5 bg-background-50 border border-background-200/70 rounded text-sm text-foreground-900 focus:outline-none focus:border-primary-300" />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* SEO */}
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 space-y-4">
          <h3 className="text-base font-bold font-heading text-foreground-900 pb-3 border-b border-background-200/70 flex items-center gap-2">
            <i className="ri-google-line text-secondary-600"></i>
            Référencement Google (SEO)
          </h3>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Titre SEO</label>
            <input type="text" value={seoTitle} onChange={(e) => setSeoTitle(e.target.value)} maxLength={70}
              placeholder="Titre affiché dans les résultats Google"
              className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors" />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Meta description</label>
            <textarea value={seoDescription} onChange={(e) => setSeoDescription(e.target.value)} maxLength={180} rows={2}
              placeholder="Résumé incitatif affiché sous le titre dans Google"
              className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Mots-clés (séparés par des virgules)</label>
            <input type="text" value={seoKeywords} onChange={(e) => setSeoKeywords(e.target.value)}
              placeholder="Ex : t-shirt coton, vêtement homme, mode"
              className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors" />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button type="submit" disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer">
            {saving ? (<><i className="ri-loader-4-line animate-spin"></i>{t('prf.saving', { defaultValue: 'Enregistrement...' })}</>) : (<><i className="ri-check-line"></i>{t('prf.save', { defaultValue: 'Enregistrer' })}</>)}
          </button>
          <button type="button" onClick={() => navigate('/dashboard/products')}
            className="px-4 py-2.5 bg-background-100 text-foreground-600 rounded-full text-sm font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer">
            {t('prf.cancel', { defaultValue: 'Annuler' })}
          </button>
        </div>
      </form>

      <MediaLibraryPicker
        isOpen={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        ownerId={getCommerceId(user)}
        onSelect={(urls) => { if (urls[0]) setMediaUrl(urls[0]); }}
        multi={false}
        initialSelected={mediaUrl ? [mediaUrl] : []}
      />
    </div>
  );
}